/**
 * BillableItemTemplateQuickAdd Component
 * Phase 8 Batch 8.6 - Billable Item Templates
 *
 * Quick-add dropdown for creating billable items from template library.
 * Auto-saves immediately when template is selected (no confirmation step).
 *
 * Context-aware item_status logic:
 * - Templates (is_template = true) -> 'proposed'
 * - Proposals (workflow_stage = 'proposal_draft' or 'proposal_sent') -> 'proposed'
 * - Active/Complete projects -> 'confirmed'
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useLibraryTemplates,
  type BillableItemTemplate,
} from '@/hooks/useBillableItemTemplates';
import { useCreateBillableItem } from '@/hooks/useBillableItems';
import {
  getCategoryDisabledReason,
  type BillableItemCategory,
} from '@/components/billable-items/CategorySelect';
import type { Project } from '@/lib/schemas';

// Category labels for display
const CATEGORY_LABELS: Record<string, string> = {
  professional_fee: 'Professional Fee',
  add_service: 'Additional Service',
  billable_expense: 'Billable Expense',
  pass_through: 'Pass Through',
  internal_work: 'Internal Work',
};

interface BillableItemTemplateQuickAddProps {
  projectId: string;
  projectNumber: number | null | undefined;
  project: Project;
  disabled?: boolean;
}

export function BillableItemTemplateQuickAdd({
  projectId,
  projectNumber,
  project,
  disabled = false,
}: BillableItemTemplateQuickAddProps) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useLibraryTemplates();
  const createItem = useCreateBillableItem();
  const templates = data?.templates || [];

  /**
   * Determine item_status based on project context
   *
   * - Templates: items start as 'proposed' (become real when template is used)
   * - Proposals: items are 'proposed' (work not yet confirmed)
   * - Active/complete projects: items are 'confirmed' (real confirmed work)
   */
  const getItemStatus = (): 'proposed' | 'confirmed' => {
    // Templates: items start as 'proposed' (become real when template is used)
    if (project.is_template) {
      return 'proposed';
    }

    // Proposals: items are 'proposed' (work not yet confirmed)
    if (
      !project.proposal_accepted_at &&
      ['proposal_draft', 'proposal_sent', 'proposal_declined'].includes(project.workflow_stage ?? '')
    ) {
      return 'proposed';
    }

    // Active/complete projects: items are 'confirmed' (real confirmed work)
    return 'confirmed';
  };

  const getTemplateCategory = (template: BillableItemTemplate): BillableItemCategory => {
    const isAdditionalService = template.category === 'professional_fee' && !project.is_template && (
      Boolean(project.proposal_accepted_at) || !project.workflow_stage ||
      getCategoryDisabledReason(template.category, project.workflow_stage ?? undefined, false) !== null
    );
    return isAdditionalService ? 'add_service' : template.category;
  };

  const handleSelectTemplate = (template: BillableItemTemplate) => {
    // Immediately create billable item from template
    createItem.mutate(
      {
        project_id: projectId,
        project_number: projectNumber ?? undefined,
        description: template.description,
        amount: template.suggested_amount,
        deliverables: template.deliverables ?? null,
        category: getTemplateCategory(template),
        source_template_id: template.id,
        cancelled: false,
        status: 'not_started',
        status_notes: null,
        item_status: getItemStatus(),
      },
      {
        onSuccess: () => {
          // Toast shown by useCreateBillableItem, but we add specific context
          toast.success(`Added "${template.description}" from template`);
          setOpen(false); // Close after successful creation
        },
        onError: (error: any) => {
          // Keep popover open so user can retry
          toast.error(`Failed to add item: ${error.message || 'Unknown error'}`);
        },
      }
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const isDisabled = disabled || templates.length === 0 || createItem.isPending;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={isDisabled}
          aria-label="Add from template"
        >
          {createItem.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Adding...
            </>
          ) : (
            <>
              <Plus className="mr-2 h-4 w-4" />
              Add from Template
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <Command>
          <CommandInput placeholder="Search templates..." />
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Loading templates...' : 'No templates found. Create templates in Settings.'}
            </CommandEmpty>
            <CommandGroup>
              {templates.map((template) => {
                const category = getTemplateCategory(template);
                const converted = category !== template.category;
                const itemDisabled = createItem.isPending;

                return (
                  <CommandItem
                    key={template.id}
                    value={template.description}
                    onSelect={() => handleSelectTemplate(template)}
                    disabled={itemDisabled}
                    title={converted ? "Adds as an Additional Service; the saved template stays a Professional Fee." : undefined}
                    className="cursor-pointer"
                  >
                    <div className="flex flex-col gap-1 w-full">
                      <div className="font-medium">
                        {template.description}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <span>{formatCurrency(template.suggested_amount)}</span>
                        <span className="text-muted-foreground/50">|</span>
                        <span>{CATEGORY_LABELS[category] || category}</span>
                      </div>
                      {template.deliverables && (
                        <div className="text-xs text-muted-foreground/70 truncate max-w-[250px]">
                          {template.deliverables.split('\n')[0]}
                          {template.deliverables.includes('\n') && '...'}
                        </div>
                      )}
                      {converted && (
                        <div className="text-xs text-muted-foreground/70">From Professional Fee template</div>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
